import type { Ref, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { useFilterTutors } from '@entities/tutor'

import { LESSONS_UNASSIGNED_TUTOR_ID } from '../../lib/lessons-query'

interface LessonFilterTutorsOptions {
  selectedIds: Ref<number[]>
}

interface LessonFilterTutorsReturn {
  refSelectedIds: WritableComputedRef<number[] | undefined>
  tutorOptions: ComputedRef<AppSelectOption<number>[]>
  selectedTutorOptions: ComputedRef<AppSelectOption<number>[]>
  tutorHasMore: ComputedRef<boolean>
  tutorLoading: Readonly<Ref<boolean>>
  onTutorSearch: (query: string) => void
  onTutorLoadMore: (query: string) => void
  onTutorOpen: () => void
}

const UNASSIGNED_OPTION: AppSelectOption<number> = {
  value: LESSONS_UNASSIGNED_TUTOR_ID,
  text: 'Unassigned tutor',
}

/** The shared tutor filter plus the legacy "Unassigned tutor" option, which the tutors list never returns. Only real
 *  tutor ids reach the shared filter: 0 is not a tutor and would be refused as `preselected`. */
export const useLessonFilterTutors = ({ selectedIds }: LessonFilterTutorsOptions): LessonFilterTutorsReturn => {
  const refIsUnassigned = computed<boolean>(() => selectedIds.value.includes(LESSONS_UNASSIGNED_TUTOR_ID))

  const refTutorIds = computed<number[]>({
    get: () => selectedIds.value.filter(id => id !== LESSONS_UNASSIGNED_TUTOR_ID),
    set: (ids) => {
      selectedIds.value = refIsUnassigned.value ? [LESSONS_UNASSIGNED_TUTOR_ID, ...ids] : ids
    },
  })

  const {
    tutorOptions: baseOptions,
    selectedTutorOptions: baseSelectedOptions,
    tutorHasMore,
    tutorLoading,
    onTutorSearch,
    onTutorLoadMore,
    onTutorOpen,
  } = useFilterTutors({ selectedIds: refTutorIds })

  const refSelectedIds = computed<number[] | undefined>({
    get: () => selectedIds.value,
    set: (value) => {
      selectedIds.value = value ?? []
    },
  })

  const tutorOptions = computed<AppSelectOption<number>[]>(() => [UNASSIGNED_OPTION, ...baseOptions.value])

  const selectedTutorOptions = computed<AppSelectOption<number>[]>(() =>
    refIsUnassigned.value ? [UNASSIGNED_OPTION, ...baseSelectedOptions.value] : baseSelectedOptions.value)

  return {
    refSelectedIds,
    tutorOptions,
    selectedTutorOptions,
    tutorHasMore,
    tutorLoading,
    onTutorSearch,
    onTutorLoadMore,
    onTutorOpen,
  }
}
